import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import LoginPage from "@/pages/LoginPage";
import HomePage from "@/pages/HomePage";
import BranchesPage from "@/pages/BranchesPage";
import StudentsPage from "@/pages/StudentsPage";
import AttendancePage from "@/pages/AttendancePage";
import StaffPage from "@/pages/StaffPage";
import StaffProfilePage from "@/pages/StaffProfilePage";
import FeesPage from "@/pages/FeesPage";
import ParentPortalPage from "@/pages/ParentPortalPage";
import CircularsPage from "@/pages/CircularsPage";
import TimetablePage from "@/pages/TimetablePage";
import GradebookPage from "@/pages/GradebookPage";
import ReceiptPrintPage from "@/pages/ReceiptPrintPage";
import DailyUpdatesPage from "@/pages/DailyUpdatesPage";
import HomeworkPage from "@/pages/HomeworkPage";
import GalleryPage from "@/pages/GalleryPage";
import EventsPage from "@/pages/EventsPage";
import MessagesPage from "@/pages/MessagesPage";
import LeavePage from "@/pages/LeavePage";
import PayrollPage from "@/pages/PayrollPage";
import SettingsPage from "@/pages/SettingsPage";
import ImportPage from "@/pages/ImportPage";
import KitsPage from "@/pages/KitsPage";
import ParentAccountsPage from "@/pages/ParentAccountsPage";
import ViewAsPage from "@/pages/ViewAsPage";
import { ProfilePage, MorePage, ReportsPage } from "@/pages/MiscPages";
import FeeManagementPage from "@/pages/FeeManagementPage";
import ParentFeesPage from "@/pages/ParentFeesPage";
import HRPayrollPage from "@/pages/HRPayrollPage";
import AdvanceRequestPage from "@/pages/AdvanceRequestPage";
import StaffAttendancePage from "@/pages/StaffAttendancePage";
import TasksPage from "@/pages/TasksPage";
import ClassDiaryPage from "@/pages/ClassDiaryPage";
import ParentDiaryPage from "@/pages/ParentDiaryPage";
import MonitorPage from "@/pages/MonitorPage";
import DiaryAdminPage from "@/pages/DiaryAdminPage";
import NotificationsPage from "@/pages/NotificationsPage";
import StudentUpdatesPage from "@/pages/StudentUpdatesPage";
import ProgressReportsPage from "@/pages/ProgressReportsPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import NoticesPage from "@/pages/NoticesPage";
import TransportPage from "@/pages/TransportPage";

const ADMIN = ["master_admin", "super_admin"];
const STAFF = ["master_admin", "super_admin", "principal", "teacher", "accountant", "hr_manager"];
const CLASS = ["master_admin", "super_admin", "principal", "teacher", "coordinator"];

const P = (el, allow) => <ProtectedRoute allow={allow}>{el}</ProtectedRoute>;

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={P(<HomePage />)} />
              <Route path="/branches" element={P(<BranchesPage />, ADMIN)} />
              <Route path="/students" element={P(<StudentsPage />, [...STAFF, "coordinator"])} />
              <Route path="/attendance" element={P(<AttendancePage />, CLASS)} />
              <Route path="/staff" element={P(<StaffPage />, [...ADMIN, "principal", "hr_manager"])} />
              <Route path="/staff/:id" element={P(<StaffProfilePage />, [...ADMIN, "principal", "hr_manager"])} />
              <Route path="/fees" element={P(<FeesPage />, [...ADMIN, "accountant", "principal"])} />
              <Route path="/fee-management" element={P(<FeeManagementPage />, [...ADMIN, "accountant", "principal"])} />
              <Route path="/hr-payroll" element={P(<HRPayrollPage />, [...ADMIN, "hr_manager", "accountant"])} />
              <Route path="/tasks" element={P(<TasksPage />, CLASS)} />
              <Route path="/diary" element={P(<ClassDiaryPage />, CLASS)} />
              <Route path="/my-diary" element={P(<ParentDiaryPage />, ["parent"])} />
              <Route path="/monitor" element={P(<MonitorPage />, [...ADMIN, "principal", "coordinator"])} />
              <Route path="/diary-admin" element={P(<DiaryAdminPage />, [...ADMIN, "principal", "coordinator"])} />
              <Route path="/student-updates" element={P(<StudentUpdatesPage />, [...CLASS, "parent"])} />
              <Route path="/progress" element={P(<ProgressReportsPage />, [...CLASS, "parent"])} />
              <Route path="/approvals" element={P(<ApprovalsPage />, [...CLASS, "center_head"])} />
              <Route path="/transport" element={P(<TransportPage />, [...ADMIN, "principal", "teacher", "coordinator", "accountant", "hr_manager", "transport_staff", "parent"])} />
              <Route path="/notices" element={P(<NoticesPage />)} />
              <Route path="/notifications" element={P(<NotificationsPage />)} />
              <Route path="/staff-attendance" element={P(<StaffAttendancePage />, [...ADMIN, "principal", "hr_manager", "coordinator"])} />
              <Route path="/advance-salary" element={P(<AdvanceRequestPage />, ["principal", "teacher", "accountant", "hr_manager"])} />
              <Route path="/my-fees" element={P(<ParentFeesPage />, ["parent"])} />
              <Route path="/portal" element={P(<ParentPortalPage />, ["parent"])} />
              <Route path="/circulars" element={P(<CircularsPage />)} />
              <Route path="/timetable" element={P(<TimetablePage />, CLASS)} />
              <Route path="/gradebook" element={P(<GradebookPage />, CLASS)} />
              <Route path="/receipt/:id" element={P(<ReceiptPrintPage />)} />
              <Route path="/updates" element={P(<DailyUpdatesPage />, [...CLASS, "parent"])} />
              <Route path="/homework" element={P(<HomeworkPage />, [...CLASS, "parent"])} />
              <Route path="/gallery" element={P(<GalleryPage />, [...CLASS, "parent"])} />
              <Route path="/events" element={P(<EventsPage />)} />
              <Route path="/messages" element={P(<MessagesPage />)} />
              <Route path="/leave" element={P(<LeavePage />)} />
              <Route path="/payroll" element={P(<PayrollPage />, [...ADMIN, "hr_manager"])} />
              <Route path="/settings" element={P(<SettingsPage />, ADMIN)} />
              <Route path="/imports" element={P(<ImportPage />, ["master_admin", "super_admin"])} />
              <Route path="/view-as" element={P(<ViewAsPage />, ADMIN)} />
              <Route path="/parent-accounts" element={P(<ParentAccountsPage />, ADMIN)} />
              <Route path="/kits" element={P(<KitsPage />, [...ADMIN, "center_head", "principal", "coordinator", "accountant"])} />
              <Route path="/reports" element={P(<ReportsPage />, [...ADMIN, "principal", "accountant", "hr_manager", "teacher"])} />
              <Route path="/profile" element={P(<ProfilePage />)} />
              <Route path="/more" element={P(<MorePage />)} />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
